import { Notice } from '@/components/ui/notice';
import { useToast } from '@/components/ui/use-toast';
import { Dialog } from '@/features/dialog/dialog';
import { DialogForm } from '@/features/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CrmEventDetails } from '@/server/types/infer-trpc-types';
import { useForm } from 'react-hook-form';
import { useUpdateEvent } from '../hooks/use-update-event';
import { getInputFromCrmEventDetails } from '../utils/get-input-from-crm-event-details';
import type { EventFormData } from './event-form-fields';
import { EventFormFields } from './event-form-fields';

export interface EventEditDialogProps {
    eventId: string;
    eventDetails: CrmEventDetails;
    onUpdated?: () => void;
}

export const EventEditDialog = DialogStateManager.create<EventEditDialogProps>(
    ({ eventId, eventDetails, onUpdated }) => {
        const { t } = useLocale('events');
        const { toast } = useToast();
        const { visible, remove } = useDialog();

        const updateEventMutation = useUpdateEvent({
            onSuccess: () => {
                remove();
                onUpdated?.();
            },
            onError: (message) => {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: message || 'An error occurred',
                });
            },
        });

        const form = useForm<EventFormData>({
            defaultValues: getInputFromCrmEventDetails(eventDetails),
            mode: 'onBlur',
        });

        const { handleSubmit } = form;

        const onSubmit = (data: EventFormData) => {
            updateEventMutation.mutate({ eventId, data });
        };

        return (
            <Dialog open={visible} onClose={remove} showClose>
                <DialogForm className="sm:max-w-2xl">
                    <DialogForm.Header title={t('events__form_edit_title')} />
                    <DialogForm.Content>
                        {!!form.watch('recurrence') && (
                            <Notice
                                variant="info"
                                title={t('events__recurrence_notice_title')}
                                description={t('events__recurrence_notice_description')}
                            />
                        )}
                        <EventFormFields
                            form={form}
                            customer={eventDetails.customer}
                            ownerName={eventDetails.owner_name ?? ''}
                        />
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        onCancel={remove}
                        onSubmit={handleSubmit(onSubmit)}
                        disabled={updateEventMutation.isLoading}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
